// PlanComponents/BudgetDistributionCard.js
import React, { useState } from 'react';
import { Typography, Table, TableBody, TableRow, TableCell, Box, LinearProgress, Button, Slider, CircularProgress, Tooltip, Fade } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import BarChartIcon from '@mui/icons-material/BarChart';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { bg, text as textColors, border } from '../../../utils/darkModeAdapter';

import CardItem from './CardItem';
import AdviceContentBox from '../../../components/AdviceContentBox';
import { toManYenLabel } from './utils';
// -------------------------------------------------------------
// 予算配分用LinearProgress
// -------------------------------------------------------------

function DistProgress({ percent = 0,
  label,
  color = '#42a5f5',
  amountYen = 0
}) {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ flex: 1 }}>
        <LinearProgress
          variant="determinate"
          value={percent}
          sx={{
            height: 12,
            borderRadius: 6,
            backgroundColor: 'rgba(0,0,0,0.05)',
            '& .MuiLinearProgress-bar': {
              backgroundColor: color,
              borderRadius: 6
            },
          }}
        />
      </Box>
      <Typography variant="body2" sx={{
        width: 70,
        textAlign: 'right',
        whiteSpace: 'nowrap', // 改行を防止
        fontWeight: 600,
        color: textColors(theme).secondary
      }}>
        {toManYenLabel(amountYen)}
      </Typography>
    </Box>
  );
}


export default function BudgetDistributionCard({ distribution, adv, totalBudgetYen = 0, onRegenerate, isRegenerating }) {
  const theme = useTheme();
  // 各割合 × 総予算で計算
  const mediaYen = Math.floor(totalBudgetYen * (distribution.media / 100));
  const agencyYen = Math.floor(totalBudgetYen * (distribution.agency / 100));
  const othersYen = Math.floor(totalBudgetYen * (distribution.others / 100));

  // 編集モード管理
  const [isEditing, setIsEditing] = useState(false);
  const [tempDistribution, setTempDistribution] = useState({ ...distribution });

  // 割合の調整
  const handleSliderChange = (type) => (e, newValue) => {
    // スライダーを動かした割合を計算
    const diff = newValue - tempDistribution[type];
    
    // 他の2つの割合を調整（均等に減らす）
    const otherTypes = Object.keys(tempDistribution).filter(key => key !== type);
    const adjustValue = Math.floor(diff / otherTypes.length);
    
    const newDistribution = { ...tempDistribution };
    
    // 変更対象を更新
    newDistribution[type] = newValue;
    
    // 他の項目を調整
    otherTypes.forEach(key => {
      const newVal = Math.max(0, tempDistribution[key] - adjustValue);
      newDistribution[key] = newVal;
    });
    
    // 合計が100になるように調整
    const total = Object.values(newDistribution).reduce((sum, val) => sum + val, 0);
    if (total !== 100) {
      // 差分を最後の項目に割り当てる
      const lastKey = otherTypes[otherTypes.length - 1];
      newDistribution[lastKey] = Math.max(0, newDistribution[lastKey] + (100 - total));
    }
    
    setTempDistribution(newDistribution);
  };
  
  // 再生成
  const handleRegenerate = () => {
    if (JSON.stringify(distribution) !== JSON.stringify(tempDistribution)) {
      onRegenerate && onRegenerate('budgetDistribution', JSON.stringify(tempDistribution));
      setIsEditing(false);
    }
  };

  // 予算配分のテンプレート適用
  const applyBudgetTemplate = (template) => {
    let newDistribution;
    
    switch (template) {
      case 'media':
        // 媒体中心
        newDistribution = { media: 70, agency: 20, others: 10 };
        break;
      case 'agency':
        // 人材紹介中心
        newDistribution = { media: 20, agency: 70, others: 10 };
        break;
      case 'balanced':
        // バランス型
        newDistribution = { media: 50, agency: 40, others: 10 };
        break;
      default:
        return;
    }
    
    setTempDistribution(newDistribution);
    // 422エラー修正: オブジェクトをJSON文字列に変換して送信
    onRegenerate && onRegenerate('budgetDistribution', JSON.stringify(newDistribution));
  };
  
  return (
    <CardItem
      title="予算配分の試算"
      adviceContent={<AdviceContentBox adviceObj={adv.budgetDistribution} />}
    >
      {!distribution ? (
        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
          未設定
        </Typography>
      ) : (
        <>
          {isEditing ? (
            // 編集モード
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: bg(theme).subtle, border: `1px solid ${border(theme).light}` }}>
                <Typography variant="subtitle2" sx={{ mb: 2, color: '#4e7df3', fontWeight: 600 }}>
                  予算配分を調整する
                </Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ width: '30%', border: 'none', pb: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#ff7043', display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BarChartIcon fontSize="small" />
                          媒体費 ({tempDistribution.media}%)
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: '70%', border: 'none', pb: 2 }}>
                        <Slider
                          value={tempDistribution.media}
                          onChange={handleSliderChange('media')}
                          aria-labelledby="media-slider"
                          valueLabelDisplay="auto"
                          step={5}
                          min={0}
                          max={100}
                          sx={{ 
                            color: '#ff7043',
                            '& .MuiSlider-valueLabel': {
                              backgroundColor: '#ff7043',
                            }
                          }}
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ border: 'none', pb: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#ab47bc', display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BusinessCenterIcon fontSize="small" />
                          人材紹介費 ({tempDistribution.agency}%)
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ border: 'none', pb: 2 }}>
                        <Slider
                          value={tempDistribution.agency}
                          onChange={handleSliderChange('agency')}
                          aria-labelledby="agency-slider"
                          valueLabelDisplay="auto"
                          step={5}
                          min={0}
                          max={100}
                          sx={{ 
                            color: '#ab47bc',
                            '& .MuiSlider-valueLabel': {
                              backgroundColor: '#ab47bc',
                            }
                          }}
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ border: 'none' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#42a5f5', display: 'flex', alignItems: 'center', gap: 1 }}>
                          <RefreshIcon fontSize="small" />
                          その他 ({tempDistribution.others}%)
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ border: 'none' }}>
                        <Slider
                          value={tempDistribution.others}
                          onChange={handleSliderChange('others')}
                          aria-labelledby="others-slider"
                          valueLabelDisplay="auto"
                          step={5}
                          min={0}
                          max={100}
                          sx={{ 
                            color: '#42a5f5',
                            '& .MuiSlider-valueLabel': {
                              backgroundColor: '#42a5f5',
                            }
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    onClick={() => setIsEditing(false)}
                    sx={{
                      borderRadius: 2,
                      borderColor: '#d0d5dd',
                      color: '#606978',
                      '&:hover': {
                        borderColor: '#babeca',
                        backgroundColor: bg(theme).subtle
                      }
                    }}
                  >
                    キャンセル
                  </Button>
                  <Button 
                    variant="contained" 
                    size="small" 
                    onClick={handleRegenerate}
                    startIcon={<AutoFixHighIcon />}
                    sx={{
                      borderRadius: 2,
                      boxShadow: '0 4px 10px rgba(78, 125, 243, 0.3)',
                      background: 'linear-gradient(90deg, #4e7df3, #21CBF3)',
                      fontWeight: 600,
                      '&:hover': {
                        boxShadow: '0 6px 15px rgba(78, 125, 243, 0.4)',
                        background: 'linear-gradient(90deg, #4065d0, #21CBF3)',
                      }
                    }}
                  >
                    この配分で再考
                  </Button>
                </Box>
              </Box>
            </motion.div>
          ) : (
            // 表示モード
            <>
              <Box sx={{ mb: 2, mt: 0.5 }}>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ width: '30%', border: 'none', py: 1.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#455a79', display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BarChartIcon fontSize="small" sx={{ color: '#ff7043' }} />
                          媒体費
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#7b8795', ml: 3.5 }}>
                          (例: 掲載費、広告費など)
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: '70%', border: 'none', py: 1.5 }}>
                        <DistProgress percent={distribution.media} label="媒体費" color="#ff7043" amountYen={mediaYen} />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ border: 'none', py: 1.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#455a79', display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BusinessCenterIcon fontSize="small" sx={{ color: '#ab47bc' }} />
                          人材紹介費
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#7b8795', ml: 3.5 }}>
                          (例: 成果報酬など)
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ border: 'none', py: 1.5 }}>
                        <DistProgress percent={distribution.agency} label="人材紹介費" color="#ab47bc" amountYen={agencyYen} />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ border: 'none', py: 1.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#455a79', display: 'flex', alignItems: 'center', gap: 1 }}>
                          <RefreshIcon fontSize="small" sx={{ color: '#42a5f5' }} />
                          その他
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#7b8795', ml: 3.5 }}>
                          (例: システムの導入費用など)
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ border: 'none', py: 1.5 }}>
                        <DistProgress percent={distribution.others} label="その他" color="#42a5f5" amountYen={othersYen} />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
              
              {/* 再考ボタン群 */}
              <Box sx={{ mt: 3, mb: 0.5, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                {isRegenerating ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#4e7df3' }}>
                      再生成中...
                    </Typography>
                    <CircularProgress size={24} sx={{ color: '#4e7df3' }} />
                  </Box>
                ) : (
                  <>
                    <Tooltip title="予算配分をカスタム編集" placement="top" arrow TransitionComponent={Fade}>
                      <Button
                        startIcon={<EditIcon />}
                        variant="outlined"
                        size="small"
                        color="primary"
                        onClick={() => {
                          setTempDistribution({ ...distribution });
                          setIsEditing(true);
                        }}
                        sx={{
                          borderRadius: 2,
                          px: 2,
                          borderColor: '#4e7df3',
                          color: '#4e7df3',
                          '&:hover': {
                            borderColor: '#21CBF3',
                            backgroundColor: 'rgba(78, 125, 243, 0.04)'
                          }
                        }}
                      >
                        配分を編集
                      </Button>
                    </Tooltip>
                    <Tooltip title="媒体費を中心とした配分に変更" placement="top" arrow TransitionComponent={Fade}>
                      <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        onClick={() => applyBudgetTemplate('media')}
                        sx={{
                          borderRadius: 2,
                          px: 2,
                          backgroundColor: '#ff7043',
                          fontWeight: 600,
                          '&:hover': {
                            backgroundColor: '#f4683b'
                          }
                        }}
                      >
                        媒体中心に
                      </Button>
                    </Tooltip>
                    <Tooltip title="人材紹介費を中心とした配分に変更" placement="top" arrow TransitionComponent={Fade}>
                      <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        onClick={() => applyBudgetTemplate('agency')}
                        sx={{
                          borderRadius: 2,
                          px: 2,
                          backgroundColor: '#ab47bc',
                          fontWeight: 600,
                          '&:hover': {
                            backgroundColor: '#9d3cad'
                          }
                        }}
                      >
                        紹介中心に
                      </Button>
                    </Tooltip>
                    <Tooltip title="バランスの取れた配分に変更" placement="top" arrow TransitionComponent={Fade}>
                      <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        onClick={() => applyBudgetTemplate('balanced')}
                        sx={{
                          borderRadius: 2,
                          px: 2,
                          background: 'linear-gradient(90deg, #4e7df3, #21CBF3)',
                          fontWeight: 600,
                          '&:hover': {
                            background: 'linear-gradient(90deg, #4065d0, #21CBF3)',
                          }
                        }}
                      >
                        バランス良く
                      </Button>
                    </Tooltip>
                  </>
                )}
              </Box>
            </>
          )}
        </>
      )}
    </CardItem>
  );
}